/**
 * A fetch that always comes back.
 *
 * `AbortSignal.timeout` is not on every phone in this fleet, so the fallback is
 * built by hand rather than assumed. The point is not elegance: it is that no
 * code path in this app can be left awaiting a reply that never arrives.
 *
 * Two budgets. API calls are small and must feel immediate, so they get a short
 * one; a photo upload over a weak connection legitimately takes longer and is
 * given room before being given up on and queued for later.
 */
export const API_TIMEOUT_MS = 12_000;
export const UPLOAD_TIMEOUT_MS = 45_000;

export async function fetchWithTimeout(
  input: RequestInfo | URL,
  init: RequestInit = {},
  ms: number = API_TIMEOUT_MS,
): Promise<Response> {
  // An AbortSignal the caller may already have supplied must still work.
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), ms);
  const existing = init.signal;
  if (existing) {
    if (existing.aborted) controller.abort();
    else existing.addEventListener('abort', () => controller.abort(), { once: true });
  }
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } catch (err) {
    // An abort from the timer reads as a normal network failure to callers, so
    // a queued item is retried rather than treated as a definitive refusal.
    if ((err as Error)?.name === 'AbortError') {
      throw new Error('network_timeout');
    }
    throw err;
  } finally {
    clearTimeout(timer);
  }
}
